import { Button } from "@/components/ui/button";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { NotAvailableState, PermissionDeniedState } from "@/components/ui/system-state";
import type { ApiErrorInfo } from "@/lib/api";
import { isDeniedFailure } from "@/lib/api-failure";
import { INVOICES_LIST_COPY } from "./copy";

/**
 * Refusal codes that mean "you may not" but that `classifyFailure` does not
 * know. The invoicing contract's `Problem.code` for a 403 is `FORBIDDEN` — its
 * own generic transport code (`types.ts`, `Problem`) — while `api-failure.ts`
 * models the gateway's `AUTH_FORBIDDEN` spelling. A 403 must render the denial
 * state whichever of the two the edge sends, so both are checked here rather
 * than widening a shared module every other page reads.
 */
const INVOICING_DENIED_CODES = new Set(["FORBIDDEN"]);

function isDenied(error: ApiErrorInfo): boolean {
  return isDeniedFailure(error) || INVOICING_DENIED_CODES.has(error.reasonCode);
}

/**
 * One failed invoicing read, rendered as the right state.
 *
 * A 403 (or the contract's own scope/role refusal codes) is a denial and gets
 * `PermissionDeniedState`; anything else is "the route did not answer" and gets
 * `NotAvailableState` with the refusal's reason code, exactly as the sibling
 * hot-wallets pages render an unbuilt backend. Both carry `SupportDetails`, so
 * the operator leaves with the support reference rather than a screenshot.
 *
 * The reason code is a bounded string from `boundedErrorText` (or `HTTP_<status>`)
 * and is passed to `NotAvailableState`, which renders it as text — never as
 * markup. An unrecognised code is therefore shown verbatim and inert.
 *
 * This decides only what to *show*. It never retries a step-up, never re-issues
 * a refused mutation and never widens a permission: the retry re-reads the same
 * route with the same session.
 */
export function InvoicingReadFailure({
  error,
  subject,
  onRetry,
}: {
  error: ApiErrorInfo;
  /** What could not be read, lower case, e.g. "the receivables list". */
  subject: string;
  onRetry?: () => void;
}) {
  const copy = INVOICES_LIST_COPY.failure;

  if (isDenied(error)) {
    return (
      <div className="flex flex-col items-center gap-2">
        <PermissionDeniedState
          title={copy.deniedTitle}
          description={copy.deniedDescription}
          details={[
            { k: copy.deniedRequired, v: copy.deniedRequiredValue },
            ...(error.capability ? [{ k: copy.deniedNeeds, v: error.capability }] : []),
          ]}
          action={
            onRetry ? (
              <Button variant="secondary" onClick={onRetry}>
                {copy.retry}
              </Button>
            ) : undefined
          }
        />
        <SupportDetails reference={error.supportReference} className="justify-center" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <NotAvailableState
        title={copy.listTitle}
        description={copy.listDescription}
        reason={`${subject}: ${error.reasonCode}`}
      />
      <SupportDetails
        reference={error.supportReference}
        capability={error.capability}
        className="justify-center"
      />
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          {copy.retry}
        </Button>
      )}
    </div>
  );
}

/**
 * A refused *action* (an export or a duplicate), shown inline above the list
 * rather than replacing it: the rows already loaded stay on screen, because the
 * list read succeeded and only the write was refused.
 */
export function ActionRefusal({
  title,
  error,
  onDismiss,
}: {
  title: string;
  error: ApiErrorInfo;
  onDismiss: () => void;
}) {
  return (
    <div
      role="alert"
      className="mb-4 rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-danger">{title}</div>
          <p className="mt-0.5 text-danger">
            {error.reasonCode === "NETWORK_ERROR"
              ? "The request never reached the service, so nothing was changed."
              : "The service refused this request; nothing was changed."}
          </p>
          {/* `reasonCode` is a bounded wire string; it is rendered as text. */}
          <p className="mt-1 font-mono text-xs text-danger" data-testid="action-refusal-code">
            {error.reasonCode}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
      <SupportDetails reference={error.supportReference} capability={error.capability} />
    </div>
  );
}
